import { useRef, useState } from 'react';
import { formatLongDate } from '~/lib/date';
import { useLocale, useT } from '~/i18n';
import type {
  EnglishResult,
  RecordingSummary,
  RecordingUrl,
  ScoreDimension,
  TranscriptTurn,
} from '~/lib/englishAssessment';

/**
 * El informe de una evaluación de inglés: el MISMO para la agencia y para el
 * candidato (los dos ven lo mismo, es parte del trato). Nivel CEFR grande,
 * barras por dimensión con las clases del puntaje de la hoja de vida, el
 * resumen en el idioma de la app, las citas textuales, la grabación (si la
 * hay) y la transcripción plegada.
 *
 * La grabación nunca viene en la vista: `loadRecording` pide una URL firmada de
 * pocos minutos al servicio (que comprueba que eres la agencia que la pidió o
 * el candidato) recién cuando tocas «Escuchar». Si la URL vence a mitad de
 * camino, el reproductor pide otra una vez.
 */

const DIMENSIONS: ScoreDimension[] = ['fluency', 'grammar', 'vocabulary', 'comprehension', 'pronunciation'];

function barColor(value: number): string {
  if (value >= 61) return 'var(--mint-ink)';
  if (value >= 41) return 'var(--purple-cta)';
  return 'var(--coral-ink)';
}

function clock(secs?: number): string | null {
  if (typeof secs !== 'number' || !Number.isFinite(secs)) return null;
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function RecordingPlayer({
  recording,
  loadRecording,
}: {
  recording: RecordingSummary;
  loadRecording: () => Promise<RecordingUrl>;
}) {
  const t = useT('english').report;
  const [src, setSrc] = useState<string | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');
  const retried = useRef(false);
  const resumeAt = useRef(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const open = async () => {
    setState('loading');
    try {
      const res = await loadRecording();
      setSrc(res.url);
      setState('idle');
    } catch {
      setState('error');
    }
  };

  return (
    <div data-testid="english-recording">
      <h3 className="brand-label">{t.recording}</h3>
      {src ? (
        <audio
          ref={audioRef}
          className="mt-2 w-full"
          controls
          autoPlay
          preload="none"
          src={src}
          data-testid="english-recording-audio"
          onLoadedMetadata={() => {
            if (resumeAt.current && audioRef.current) audioRef.current.currentTime = resumeAt.current;
          }}
          onError={() => {
            // La URL firmada venció (o falló la red): una URL nueva, una vez.
            if (retried.current) {
              setState('error');
              return;
            }
            retried.current = true;
            resumeAt.current = audioRef.current?.currentTime ?? 0;
            void open();
          }}
        />
      ) : (
        <button
          type="button"
          className="btn btn-ghost btn-sm mt-1"
          disabled={state === 'loading'}
          onClick={() => void open()}
        >
          {state === 'loading' && <span className="loading loading-spinner loading-xs" />}
          {state === 'loading' ? t.recordingLoading : t.listen}
          {recording.duration_secs ? ` · ${clock(recording.duration_secs)}` : ''}
        </button>
      )}
      {state === 'error' && <p className="text-xs text-error mt-1">{t.recordingFailed}</p>}
      <p className="text-xs hush mt-1">
        {t.recordingPrivacy}
        {recording.expires_at && <> {t.recordingUntil(formatLongDate(recording.expires_at, ''))}</>}
      </p>
    </div>
  );
}

interface Props {
  result: EnglishResult;
  transcript?: TranscriptTurn[];
  /** La grabación (si la hay) y cómo pedir su URL firmada. */
  recording?: RecordingSummary;
  loadRecording?: () => Promise<RecordingUrl>;
  /** Sólo la agencia: la nota de que es una preselección, no un certificado. */
  showDisclaimer?: boolean;
  reused?: boolean;
}

export default function EnglishReport({ result, transcript = [], showDisclaimer, reused, recording, loadRecording }: Props) {
  const t = useT('english').report;
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const summary = locale === 'en' ? result.summary_en || result.summary_es : result.summary_es || result.summary_en;
  const level = result.insufficient_evidence || !result.cefr ? null : result.cefr;
  const pron = result.pronunciation ?? null;
  const pronSummary = pron ? (locale === 'en' ? pron.summary_en || pron.summary_es : pron.summary_es || pron.summary_en) : '';

  return (
    <div className="english-report space-y-4" data-testid="english-report">
      <div className="flex items-center gap-4 flex-wrap">
        <div
          className="english-report__badge"
          aria-label={level ? `${t.level}: ${level}` : t.insufficient}
          style={{
            minWidth: 72,
            height: 72,
            borderRadius: 20,
            display: 'grid',
            placeItems: 'center',
            fontWeight: 800,
            fontSize: level ? '1.9rem' : '0.8rem',
            letterSpacing: '-0.03em',
            padding: level ? 0 : '0 10px',
            textAlign: 'center',
            color: level ? 'var(--navy-deep)' : 'var(--ink-soft)',
            background: level ? 'var(--mint)' : 'var(--surface-sunk)',
          }}
        >
          {level ?? t.insufficient}
        </div>
        <div className="min-w-0">
          <span className="brand-label block">{t.level}</span>
          <p className="text-sm hush mt-1">
            {t.confidence[result.confidence] ?? ''}
            {result.scored_at && <> · {t.scoredOn(formatLongDate(result.scored_at, ''))}</>}
          </p>
        </div>
      </div>

      {reused && <p className="text-xs hush">{t.reused}</p>}

      {level ? (
        <ul className="score__bars">
          {DIMENSIONS.map((dimension) => {
            const value = result.scores?.[dimension];
            const label = t.dimensions[dimension];
            if (typeof value !== 'number') {
              return (
                <li key={dimension}>
                  <div className="score__bar-top">
                    <span className="score__bar-label">{label}</span>
                  </div>
                  <span className="score__bar-hint">
                    {result.pronunciation_reason ? t.pronunciationNotMeasured : t.pronunciationPending}
                  </span>
                </li>
              );
            }
            return (
              <li key={dimension}>
                <div className="score__bar-top">
                  <span className="score__bar-label">{label}</span>
                  <span className="score__bar-value">{value}</span>
                </div>
                <div className="score__track" role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={value}>
                  <div className="score__fill" style={{ width: `${value}%`, background: barColor(value) }} />
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm">{t.insufficientNote}</p>
      )}

      {summary && <p className="text-sm">{summary}</p>}

      {level && pron && (pronSummary || pron.issues?.length > 0) && (
        <div data-testid="english-pronunciation">
          <h3 className="brand-label">
            {t.dimensions.pronunciation} · {pron.cefr}
          </h3>
          {pronSummary && <p className="text-sm mt-1">{pronSummary}</p>}
          {pron.issues?.length > 0 && (
            <>
              <p className="text-xs hush mt-2">{t.pronunciationIssues}</p>
              <ul className="mt-1 space-y-1 text-sm">
                {pron.issues.map((issue, index) => (
                  <li key={`${issue.at}-${index}`}>
                    <span className="font-semibold">{issue.target}</span>
                    <span className="hush text-xs"> · {t.issueAt(issue.at)}</span>
                    {locale !== 'en' && issue.note_es && <span className="text-xs hush"> · {issue.note_es}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {recording?.available && loadRecording && <RecordingPlayer recording={recording} loadRecording={loadRecording} />}

      {result.evidence?.length > 0 && (
        <div>
          <h3 className="brand-label">{t.evidence}</h3>
          <ul className="mt-2 space-y-2">
            {result.evidence.map((item, index) => (
              <li
                key={`${item.dimension}-${index}`}
                className="text-sm p-3"
                style={{ background: 'var(--surface-hi)', borderRadius: 'var(--r-inner)', border: '1px solid var(--hairline)' }}
              >
                <p className="italic">“{item.quote}”</p>
                <p className="text-xs hush mt-1">
                  {t.dimensions[item.dimension]}
                  {item.note && <> · {item.note}</>}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {transcript.length > 0 && (
        <div>
          <button type="button" className="btn btn-ghost btn-sm" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
            {open ? t.hideTranscript : t.transcript}
          </button>
          {open && (
            <ol className="mt-2 space-y-2 text-sm" data-testid="english-transcript">
              {transcript.map((turn, index) => (
                <li key={index}>
                  <span className="font-semibold">{turn.role === 'candidate' ? t.candidate : t.interviewer}</span>
                  {clock(turn.t_secs) && <span className="hush text-xs"> · {clock(turn.t_secs)}</span>}
                  <p>{turn.text}</p>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {showDisclaimer && <p className="text-xs hush">{t.disclaimer}</p>}
    </div>
  );
}
